import {
  FaActionSequence,
  StructuredLogger,
} from '@brighthub/structured-logging';
import { Skeleton } from 'primeng/skeleton';
import { ACTION_API } from '../api/action-api.token';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  inject,
  OnDestroy,
  signal,
} from '@angular/core';
import {
  ActionEditorAnalytics,
  ActionEditorEvent,
} from '../../analytics/action-editor-analytics';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { AutoComplete } from 'primeng/autocomplete';
import { FormsModule } from '@angular/forms';
import { ConfirmationService } from 'primeng/api';
import { Button } from 'primeng/button';
import { Checkbox } from 'primeng/checkbox';
import { ConfirmDialog } from 'primeng/confirmdialog';
import { DynamicDialogConfig, DynamicDialogRef } from 'primeng/dynamicdialog';
import { Select } from 'primeng/select';
import { firstValueFrom, Subject, switchMap } from 'rxjs';
import {
  actionDateInputValue,
  actionTodayDateKey,
  formatActionDate,
} from '../action-date';
import {
  ActionOwner,
  ActionReferenceValue,
  CarePlanAction,
} from '../actions.model';
import {
  ActionEditorSource,
  ActionModalEntryPoint,
} from '../action-editor-source';
import { CARE_PLAN_CURRENT_USER } from '../../care-plan-current-user.token';
import { CarePlanEditorRowComponent } from '../../shared/care-plan-editor-row/care-plan-editor-row.component';
import { CarePlanEditorShellComponent } from '../../shared/care-plan-editor-shell/care-plan-editor-shell.component';
import { ActionAssigneeSearchService } from '../owners/action-assignee-search.service';
import { ACTION_OWNER_API } from '../owners/action-owner-api.token';

export interface ActionEditorDialogData {
  mode: 'add' | 'edit';
  entryPoint: ActionModalEntryPoint;
  editor: ActionEditorSource;
  action?: CarePlanAction;
  caseOptions?: { label: string; value: string | null }[];
}

const ASSIGNEE_TYPES = [
  {
    label: 'Financial Assistant',
    value: 'financial_assistant',
    disabled: false,
  },
  { label: 'Client', value: 'client', disabled: true },
  { label: 'Navi (AI agent)', value: 'navi', disabled: true },
];

@Component({
  selector: 'bn-action-editor-dialog',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    Skeleton,
    FormsModule,
    AutoComplete,
    Button,
    Checkbox,
    ConfirmDialog,
    Select,
    CarePlanEditorRowComponent,
    CarePlanEditorShellComponent,
  ],
  providers: [ConfirmationService],
  templateUrl: './action-editor-dialog.component.html',
  styleUrl: './action-editor-dialog.component.scss',
})
export class ActionEditorDialogComponent implements OnDestroy {
  private readonly _logs = inject(StructuredLogger);
  private _saveLog?: FaActionSequence;
  private readonly _actionApi = inject(ACTION_API);
  private readonly _analytics = inject(ActionEditorAnalytics);
  private _closed = false;
  private readonly _ref = inject(DynamicDialogRef);
  private readonly _config = inject(DynamicDialogConfig);
  private readonly _confirmation = inject(ConfirmationService);
  private readonly _currentUser = inject(CARE_PLAN_CURRENT_USER, {
    optional: true,
  });
  private readonly _destroyRef = inject(DestroyRef);
  private readonly _assigneeSearch = inject(ActionAssigneeSearchService);
  private readonly _ownerQuery = new Subject<string>();
  private readonly _ownerApi = inject(ACTION_OWNER_API);
  private readonly _data = this._config.data as ActionEditorDialogData;
  private readonly _eventContext = {
    ...this._data.editor.analyticsContext?.(this._data.action),
    entryPoint: this._data.entryPoint,
  };
  private readonly _resolvedOwnerName = signal<string | null>(null);

  protected readonly editor = this._data.editor;
  protected readonly mode = this._data.mode;
  protected readonly assigneeTypes = ASSIGNEE_TYPES;
  protected readonly priorities = computed(() => [...this.editor.priorities()]);
  protected readonly statuses = computed(() => [...this.editor.statuses()]);

  readonly caseOptions = this._data.caseOptions;
  readonly selectedCaseId = signal<string | null>(null);
  readonly title = signal(this._data.action?.title ?? '');
  readonly description = signal(this._data.action?.description ?? '');
  readonly descriptionError = computed(() =>
    this.submitAttempted() && this.description().length > 500
      ? 'Keep the description to 500 characters or fewer.'
      : null,
  );
  readonly assigneeType = signal('financial_assistant');
  readonly ownerLabel = computed(() => {
    const action = this._data.action;
    const currentUserName = this._currentUser?.displayName().trim();
    if (!action) return currentUserName || 'Signed-in Financial Assistant';

    const isCurrentUser =
      !!action.assignedToId && action.assignedToId === this._currentUser?.id();
    if (action.sourceTypeCode === 'HUMAN_FA' && isCurrentUser) {
      return currentUserName || this.editor.ownerLabel(action.assignedToId);
    }
    return (
      this._resolvedOwnerName() ?? this.editor.ownerLabel(action.assignedToId)
    );
  });
  private readonly _initialOwnerId = this._data.action
    ? this._data.action.assignedToId
    : (this._currentUser?.id() ?? null);
  readonly ownerInput = signal<ActionOwner | string | null>(
    this._initialOwnerId
      ? { id: this._initialOwnerId, displayName: this.ownerLabel() }
      : null,
  );
  private readonly _ownerMatches = signal<ActionOwner[]>([]);
  private readonly _ownerSearchText = signal('');
  readonly ownerSearchLoading = signal(false);
  readonly ownerSearchError = signal<string | null>(null);
  readonly savedWithAssignmentIssue = signal<CarePlanAction | null>(null);
  readonly selectedOwnerId = computed(() => {
    const value = this.ownerInput();
    return value && typeof value !== 'string' ? value.id : null;
  });
  readonly ownerError = computed(() => {
    if (!this.submitAttempted()) return null;
    if (this.selectedOwnerId()) return null;
    // Existing records without an owner can still be edited without forcing assignment.
    if (
      this.mode === 'edit' &&
      !this._initialOwnerId &&
      this.ownerInput() === null
    )
      return null;
    return 'Select an employee from the results.';
  });
  readonly ownerSuggestions = computed(() => {
    const currentId = this._currentUser?.id();
    const currentName = this._currentUser?.displayName().trim();
    const query = this._ownerSearchText().trim().toLowerCase();
    const input = this.ownerInput();
    const choices = [
      ...(currentId && currentName
        ? [{ id: currentId, displayName: currentName, isCurrentUser: true }]
        : []),
      ...(input && typeof input !== 'string' ? [input] : []),
      ...this._ownerMatches(),
    ];
    const unique = new Map<string, ActionOwner>();
    for (const owner of choices) {
      const matches =
        !query ||
        query
          .split(/\s+/)
          .every((token) =>
            `${owner.displayName} ${owner.email ?? ''}`
              .toLowerCase()
              .includes(token),
          );
      if (matches && !unique.has(owner.id)) unique.set(owner.id, owner);
    }
    return [...unique.values()].slice(0, 10);
  });

  changeOwner(value: ActionOwner | string | null): void {
    if (this.saving()) return;
    this.ownerInput.set(value);
    this.searchOwners(typeof value === 'string' ? value : '');
  }

  searchOwners(query: string): void {
    this._ownerSearchText.set(query);
    this._ownerQuery.next(query);
  }

  readonly actionTypeCode = signal<string | null>(
    this._initialReferenceCode(
      this.editor.actionTypes(),
      this._data.action?.actionTypeCode,
      'FOLLOW_UP',
    ),
  );
  readonly communicationPreferenceCode = signal<string | null>(
    this._data.action
      ? this._initialReferenceCode(
          this.editor.communicationPreferences(),
          this._data.action.communicationPreferenceCode,
        )
      : null,
  );
  readonly dueDate = signal(
    actionDateInputValue(this._data.action?.dueDate ?? null),
  );
  readonly minimumDueDate = actionTodayDateKey();
  readonly priorityCode = signal<string | null>(
    this._initialReferenceCode(
      this.editor.priorities(),
      this._data.action?.priorityCode,
      'NORMAL',
    ),
  );
  readonly statusCode = signal<string | null>(
    this._initialReferenceCode(
      this.editor.statuses(),
      this._data.action?.statusCode,
      'OPEN',
    ),
  );
  readonly clientPromise = signal(this._data.action?.clientPromise ?? false);
  readonly submitAttempted = signal(false);
  readonly saving = signal(false);
  readonly saveError = signal<string | null>(null);

  readonly actionTypes = computed(() => [...this.editor.actionTypes()]);
  readonly communicationPreferences = computed(() => [
    { code: '', label: '—' },
    ...this.editor.communicationPreferences(),
  ]);

  readonly titleError = computed(() => {
    if (!this.submitAttempted()) return null;
    const title = this.title().trim();
    if (!title) return 'Enter a title for this action.';
    if (title.length > 200) {
      return 'Keep the action title to 200 characters or fewer.';
    }
    return null;
  });
  readonly typeError = computed(() =>
    this.submitAttempted() && !this.actionTypeCode()
      ? 'Select an action type.'
      : null,
  );
  readonly dueDateError = computed(() => {
    if (!this.submitAttempted()) return null;
    const dueDate = this.dueDate();
    if (!dueDate) return 'Select a due date.';
    if (this.mode === 'add' && dueDate < this.minimumDueDate) {
      return 'Due date cannot be before today.';
    }
    return null;
  });
  readonly priorityError = computed(() =>
    this.submitAttempted() && !this.priorityCode()
      ? 'Select a priority.'
      : null,
  );

  constructor() {
    this._ownerQuery
      .pipe(
        switchMap((query) => this._assigneeSearch.search(query)),
        takeUntilDestroyed(this._destroyRef),
      )
      .subscribe((state) => {
        this._ownerMatches.set(state.owners);
        this.ownerSearchLoading.set(state.loading);
        this.ownerSearchError.set(state.error);
      });
    this._track('action_modal_opened');
    if (this.mode === 'add') this._track('action_create_started');
    void this._resolveEditOwner();
  }

  ngOnDestroy(): void {
    if (!this._closed) {
      this._track('action_modal_closed', { reason: 'dismissed' });
      this._closed = true;
    }
  }

  private _track(
    event: ActionEditorEvent,
    details: Record<string, unknown> = {},
  ): void {
    this._analytics.log(
      event,
      this.mode,
      this._eventContext,
      this._data.action,
      details,
    );
  }

  private _close(
    reason: 'saved' | 'canceled' | 'discarded',
    action?: CarePlanAction,
  ): void {
    if (this._closed) return;
    this._closed = true;
    this._analytics.log(
      'action_modal_closed',
      this.mode,
      this._eventContext,
      action ?? this._data.action,
      { reason },
    );
    if (action) this._ref.close(action);
    else this._ref.close();
  }

  readonly dirty = computed(() => {
    if (this.selectedCaseId() !== null) return true;
    const action = this._data.action;
    if (!action) {
      return (
        this.selectedOwnerId() !== this._initialOwnerId ||
        typeof this.ownerInput() === 'string' ||
        !!this.title().trim() ||
        !!this.description().trim() ||
        this.actionTypeCode() !== this._defaultActionType() ||
        !!this.communicationPreferenceCode() ||
        !!this.dueDate() ||
        this.priorityCode() !== this._defaultPriority() ||
        this.clientPromise()
      );
    }
    return (
      this.selectedOwnerId() !== this._initialOwnerId ||
      typeof this.ownerInput() === 'string' ||
      this.title().trim() !== action.title ||
      this.description().trim() !== (action.description ?? '') ||
      this.actionTypeCode() !== action.actionTypeCode ||
      this.communicationPreferenceCode() !==
        action.communicationPreferenceCode ||
      this.dueDate() !== actionDateInputValue(action.dueDate) ||
      this.priorityCode() !== action.priorityCode ||
      this.statusCode() !== action.statusCode ||
      this.clientPromise() !== action.clientPromise
    );
  });

  protected statusLabel(): string {
    return this.editor.statusLabel(this.statusCode());
  }

  protected createdLabel(): string {
    return this._data.action?.createdAt
      ? formatActionDate(this._data.action.createdAt)
      : '—';
  }

  protected updatedLabel(): string {
    return this._data.action?.updatedAt
      ? formatActionDate(this._data.action.updatedAt)
      : '—';
  }

  async submit(): Promise<void> {
    if (this.saving() || this._closed || this.savedWithAssignmentIssue())
      return;
    this.submitAttempted.set(true);
    const invalidFields = [
      ['owner', this.ownerError()],
      ['title', this.titleError()],
      ['description', this.descriptionError()],
      ['action_type', this.typeError()],
      ['priority', this.priorityError()],
      ['due_date', this.dueDateError()],
    ]
      .filter(([, error]) => !!error)
      .map(([field]) => field);
    if (invalidFields.length) {
      this._track('action_validation_failed', {
        invalid_fields: invalidFields,
      });
      return;
    }
    const actionTypeCode = this.actionTypeCode();
    const priorityCode = this.priorityCode();
    const statusCode = this.statusCode();
    if (!actionTypeCode || !priorityCode) return;

    const assignedToId = this.selectedOwnerId();
    const assignment =
      assignedToId && assignedToId !== this._initialOwnerId
        ? { assignedToId }
        : {};
    this.saving.set(true);
    this.saveError.set(null);
    const logAttempt = (this._saveLog ??= this._logs.action(
      this.mode === 'edit'
        ? 'care_plan.action.update'
        : 'care_plan.action.create',
    )).begin({
      action_id: this._data.action?.id,
      case_id: this._data.action?.caseId ?? this.selectedCaseId(),
    });
    let dispatched = false;
    try {
      if (this.mode === 'edit' && this._data.action) {
        const previous = this._data.action;
        if (!this.editor.update) {
          throw new Error('Action editing is unavailable.');
        }
        dispatched = true;
        let updated = await this.editor.update(
          previous.id,
          {
            title: this.title(),
            description: this.description().trim(),
            ...assignment,
            actionTypeCode,
            communicationPreferenceCode:
              this.communicationPreferenceCode() || null,
            dueDate: this.dueDate(),
            priorityCode,
            ...(statusCode ? { statusCode } : {}),
            clientPromise: this.clientPromise(),
          },
          { eventOwner: 'modal' },
        );
        if (
          assignment.assignedToId &&
          updated.assignedToId != null &&
          updated.assignedToId !== assignment.assignedToId
        ) {
          try {
            const refreshed = await firstValueFrom(
              this._actionApi.getAction(previous.id),
            );
            if (
              refreshed.id !== previous.id ||
              refreshed.clientId !== previous.clientId
            )
              throw new Error('The saved action could not be verified.');
            updated = refreshed;
          } catch (error) {
            logAttempt.fail(error, 'verification', true, undefined, {
              completed_count: 1,
              operation_count: 2,
            });
            // The PATCH already committed. Do not invite another write just
            // because verification failed, or count it as a failed save.
            this._analytics.saved(
              this.mode,
              this._eventContext,
              updated,
              previous,
            );
            this.savedWithAssignmentIssue.set(updated);
            this.saveError.set(
              'Your changes were saved, but the assignee could not be verified. Close and reopen the action to check before retrying.',
            );
            return;
          }
        }
        this._analytics.saved(this.mode, this._eventContext, updated, previous);
        if (
          assignment.assignedToId &&
          updated.assignedToId != null &&
          updated.assignedToId !== assignment.assignedToId
        ) {
          logAttempt.fail(null, 'verification', true, 'business_rule', {
            completed_count: 1,
            operation_count: 2,
          });
          this.saveError.set(
            'Your changes were saved, but the selected assignee was not applied. Try assigning the action again.',
          );
          this._analytics.log(
            'action_assignment_mismatch',
            this.mode,
            this._eventContext,
            updated,
          );
          return;
        }
        logAttempt.succeed('save');
        this._close('saved', updated);
      } else {
        if (!this.editor.create) {
          throw new Error('Action creation is unavailable.');
        }
        const communicationPreferenceCode = this.communicationPreferenceCode();
        dispatched = true;
        const created = await this.editor.create(
          {
            title: this.title(),
            description: this.description().trim(),
            ...(this.caseOptions ? { caseId: this.selectedCaseId() } : {}),
            ...assignment,
            actionTypeCode,
            ...(communicationPreferenceCode
              ? {
                  communicationPreferenceCode,
                }
              : {}),
            dueDate: this.dueDate(),
            priorityCode,
            clientPromise: this.clientPromise(),
          },
          { eventOwner: 'modal' },
        );
        this._analytics.saved(this.mode, this._eventContext, created);
        if (
          assignment.assignedToId &&
          created.assignedToId != null &&
          created.assignedToId !== assignment.assignedToId
        ) {
          logAttempt.fail(null, 'verification', true, 'business_rule', {
            completed_count: 1,
            operation_count: 2,
          });
          this.savedWithAssignmentIssue.set(created);
          this.saveError.set(
            'The action was created, but the selected assignee was not applied. Close this dialog and reopen the action to retry assignment.',
          );
          this._analytics.log(
            'action_assignment_mismatch',
            this.mode,
            this._eventContext,
            created,
          );
          return;
        }
        logAttempt.succeed('save');
        this._close('saved', created);
      }
    } catch (error) {
      logAttempt.fail(
        error,
        'save',
        dispatched,
        !dispatched ? 'stale_state' : undefined,
      );
      this._track('action_save_failed');
      this.saveError.set(this._saveErrorMessage(error));
    } finally {
      this.saving.set(false);
    }
  }

  private _saveErrorMessage(error: unknown): string {
    const response = error as {
      result?: { message?: unknown };
      error?: { result?: { message?: unknown }; message?: unknown };
    } | null;
    const message =
      response?.error?.result?.message ??
      response?.result?.message ??
      response?.error?.message;
    if (typeof message === 'string' && message.trim()) {
      return message.trim().slice(0, 500);
    }
    return error instanceof Error
      ? error.message
      : "Couldn't save — try again.";
  }

  tryClose(): void {
    if (this.saving() || this._closed) return;
    const saved = this.savedWithAssignmentIssue();
    if (saved) {
      this._close('saved', saved);
      return;
    }
    if (!this.dirty()) {
      this._close('canceled');
      return;
    }
    this._track('action_discard_prompted');
    this._confirmation.confirm({
      message:
        'You have unsaved changes — are you sure you want to discard them?',
      header: 'Unsaved changes',
      icon: 'pi pi-exclamation-triangle',
      acceptLabel: 'Discard',
      rejectLabel: 'Stay',
      acceptButtonStyleClass: 'p-button-danger',
      rejectButtonStyleClass: 'p-button-text',
      accept: () => {
        if (!this.saving()) this._close('discarded');
      },
      reject: () => this._track('action_discard_canceled'),
    });
  }

  private _initialReferenceCode(
    values: readonly ActionReferenceValue[],
    current: string | null | undefined,
    preferredAddCode?: string,
  ): string | null {
    if (this._data.action) {
      return current && values.some((value) => value.code === current)
        ? current
        : null;
    }
    if (preferredAddCode) {
      const preferred = values.find((value) => value.code === preferredAddCode);
      if (preferred) return preferred.code;
    }
    return values[0]?.code ?? null;
  }

  private _defaultPriority(): string | null {
    return (
      this.editor.priorities().find((priority) => priority.code === 'NORMAL')
        ?.code ??
      this.editor.priorities()[0]?.code ??
      null
    );
  }

  private _defaultActionType(): string | null {
    return (
      this.editor.actionTypes().find((type) => type.code === 'FOLLOW_UP')
        ?.code ??
      this.editor.actionTypes()[0]?.code ??
      null
    );
  }

  private async _resolveEditOwner(): Promise<void> {
    const action = this._data.action;
    if (
      !action?.assignedToId ||
      action.sourceTypeCode !== 'HUMAN_FA' ||
      action.assignedToId === this._currentUser?.id()
    ) {
      return;
    }
    const owner = await firstValueFrom(
      this._ownerApi.resolveOwner(action.assignedToId),
    );
    if (this._destroyRef.destroyed) return;
    this._resolvedOwnerName.set(owner?.displayName ?? null);
    if (owner && this.selectedOwnerId() === action.assignedToId) {
      this.ownerInput.set(owner);
    }
  }
}
